import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { leadsAPI, pipelineAPI } from "../../services/api";
import toast from "react-hot-toast";
import { format } from "date-fns";
import {
  HiOutlinePhone,
  HiOutlineUser,
  HiOutlineExclamation,
  HiOutlineDotsVertical,
} from "react-icons/hi";

/**
 * Leads Kanban view — one column per pipeline stage (Settings → Pipeline
 * Stages), leads as draggable cards. Dropping a card on another column
 * updates the lead's stage (same endpoint as the list view's "Update Stage").
 *
 * Native HTML5 drag & drop (no extra dependency).
 */

const UNKNOWN_KEY = "__unknown__";

const LeadsKanban = ({ onLeadClick }) => {
  const queryClient = useQueryClient();
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  // Pipeline stages — same query key as the Leads list page (shared cache)
  const { data: stagesData, isLoading: stagesLoading } = useQuery({
    queryKey: ["pipelineStages"],
    queryFn: async () => {
      const response = await pipelineAPI.getAll();
      return response.data?.data || [];
    },
  });
  const stages = stagesData || [];

  // All leads (no stage filter) grouped client-side into the stage columns
  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ["leads", "kanban"],
    queryFn: async () => {
      const response = await leadsAPI.getAll({ limit: 10000 });
      return response.data?.data || [];
    },
  });
  const leads = Array.isArray(leadsData) ? leadsData : [];

  // Column ordering — stages by their order, then any unmatched leads
  const columns = useMemo(() => {
    const groups = {};
    stages.forEach((s) => {
      groups[s.key] = { stage: s, leads: [] };
    });
    leads.forEach((lead) => {
      const key = lead.stage || "";
      if (groups[key]) {
        groups[key].leads.push(lead);
      } else {
        if (!groups[UNKNOWN_KEY]) {
          groups[UNKNOWN_KEY] = {
            stage: { key: UNKNOWN_KEY, name: "Unmatched", color: "#6B7280", icon: "❓" },
            leads: [],
            isUnknown: true,
          };
        }
        groups[UNKNOWN_KEY].leads.push(lead);
      }
    });
    return Object.values(groups).sort((a, b) => {
      if (a.isUnknown) return 1;
      if (b.isUnknown) return -1;
      return (a.stage.order ?? 0) - (b.stage.order ?? 0);
    });
  }, [stages, leads]);

  // Move a lead to another stage (optimistic, reverted on failure)
  const moveStageMutation = useMutation({
    mutationFn: ({ id, stage }) => leadsAPI.update(id, { stage }),
    onMutate: async ({ id, stage }) => {
      await queryClient.cancelQueries(["leads", "kanban"]);
      const previous = queryClient.getQueryData(["leads", "kanban"]);
      queryClient.setQueryData(["leads", "kanban"], (old) =>
        (old || []).map((l) => (l._id === id ? { ...l, stage } : l))
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["leads", "kanban"], context.previous);
      }
      toast.error(error?.response?.data?.message || "Failed to update stage");
    },
    onSuccess: () => {
      toast.success("Stage updated successfully");
      // Keep the shared counts in sync with the list view
      queryClient.invalidateQueries(["stageStats"]);
      queryClient.invalidateQueries(["leadsCounts"]);
    },
  });

  const handleDragStart = (e, lead) => {
    setDraggingId(lead._id);
    e.dataTransfer.setData("text/plain", lead._id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverKey(null);
  };

  const handleDrop = (e, stageKey) => {
    e.preventDefault();
    setDragOverKey(null);
    const id = e.dataTransfer.getData("text/plain") || draggingId;
    setDraggingId(null);
    if (!id) return;
    const lead = leads.find((l) => l._id === id);
    if (!lead) return;
    if (lead.stage === stageKey) return;
    if (stageKey === UNKNOWN_KEY) {
      toast.error("Please drop the lead on a pipeline stage column");
      return;
    }
    moveStageMutation.mutate({ id, stage: stageKey });
  };

  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverKey !== stageKey) setDragOverKey(stageKey);
  };

  const isLoading = stagesLoading || leadsLoading;
  const isLoadingStage =
    moveStageMutation.isPending && moveStageMutation.variables?.stage;

  return (
    <div>
      {/* Board */}
      {isLoading ? (
        <div className="py-16 flex justify-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1">
          {columns.map(({ stage, leads: columnLeads, isUnknown }) => {
            const color = stage.color || "#6B7280";
            const isDragOver = dragOverKey === stage.key && stage.key !== UNKNOWN_KEY;

            return (
              <div
                key={stage.key}
                onDragOver={(e) => handleDragOver(e, stage.key)}
                onDragLeave={(e) => {
                  // only clear when leaving the column itself
                  if (!e.currentTarget.contains(e.relatedTarget)) setDragOverKey(null);
                }}
                onDrop={(e) => handleDrop(e, stage.key)}
                className={`
                  flex-shrink-0 w-[270px] sm:w-[300px] rounded-xl border flex flex-col
                  transition-colors
                  ${isDragOver
                    ? "border-primary-400 bg-primary-50/60 ring-2 ring-primary-300"
                    : "border-gray-200 bg-gray-50"
                  }
                  ${isUnknown ? "opacity-80" : ""}
                `}
              >
                {/* Column header */}
                <div
                  className="flex items-center gap-2 px-3 py-2.5 border-b rounded-t-xl"
                  style={{
                    borderColor: `${color}33`,
                    background: `${color}14`,
                  }}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-sm font-semibold text-gray-800 truncate">
                    {stage.icon ? `${stage.icon} ` : ""}
                    {stage.name}
                  </span>
                  <span
                    className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full text-white"
                    style={{ backgroundColor: color }}
                  >
                    {columnLeads.length}
                  </span>
                </div>

                {/* Cards */}
                <div className="flex-1 p-2 space-y-2 min-h-[120px] max-h-[65vh] overflow-y-auto">
                  {columnLeads.length === 0 ? (
                    <div className="h-20 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-400">
                      {isUnknown ? "No unmatched leads" : "Drop leads here"}
                    </div>
                  ) : (
                    columnLeads.map((lead) => (
                      <div
                        key={lead._id}
                        draggable={!isUnknown}
                        onDragStart={(e) => handleDragStart(e, lead)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onLeadClick?.(lead)}
                        className={`
                          bg-white rounded-lg border border-gray-200 p-2.5 cursor-pointer
                          transition-shadow hover:shadow-md select-none
                          ${draggingId === lead._id ? "opacity-40 rotate-1" : ""}
                          ${draggingId ? "cursor-grabbing" : "cursor-grab"}
                        `}
                        style={
                          draggingId === lead._id
                            ? { borderLeft: `3px solid ${color}` }
                            : undefined
                        }
                      >
                        {/* Name + drag affordance */}
                        <div className="flex items-start gap-1.5">
                          <p className="flex-1 min-w-0 text-sm font-semibold text-gray-900 truncate">
                            {lead.name}
                          </p>
                          <HiOutlineDotsVertical className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                        </div>

                        {/* Contact */}
                        <div className="mt-1.5 space-y-1 text-xs text-gray-500">
                          <p className="flex items-center gap-1.5 truncate">
                            <HiOutlinePhone className="w-3.5 h-3.5 flex-shrink-0" />
                            {lead.phone}
                          </p>
                          {lead.assignedTo && (
                            <p className="flex items-center gap-1.5 truncate">
                              <HiOutlineUser className="w-3.5 h-3.5 flex-shrink-0" />
                              {lead.assignedTo.firstName} {lead.assignedTo.lastName}
                            </p>
                          )}
                        </div>

                        {/* Footer: date + DND warning */}
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <span className="text-[10px] text-gray-400">
                            {lead.createdAt
                              ? format(new Date(lead.createdAt), "MMM d, yyyy")
                              : ""}
                          </span>
                          {lead.dnd_status?.is_dnd && (
                            <span
                              className="flex items-center gap-1 text-[10px] font-medium text-red-500"
                              title="Do Not Disturb lead"
                            >
                              <HiOutlineExclamation className="w-3 h-3" />
                              DND
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Drop hint */}
                {isDragOver && (
                  <div className="px-3 py-2 text-xs text-primary-600 font-medium border-t border-primary-200">
                    Drop to move to “{stage.name}”
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isLoadingStage && (
        <p className="text-xs text-gray-400 mt-1">Updating stage…</p>
      )}
    </div>
  );
};

export default LeadsKanban;